/*
 * Vigor premium experience: scroll reveals, staggered grids and parallax.
 * Progressive enhancement only. Content is visible by default; an element is
 * hidden (.vg-pending) only when it starts below the fold, and it is revealed
 * as soon as it scrolls into view. Horizon's own components are untouched.
 */
(function () {
  'use strict';

  var cfg = window.vgPremium || {};
  var root = document.documentElement;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var enabled = cfg.motion !== 'off' && !reduce.matches && !cfg.design && 'IntersectionObserver' in window;

  if (!enabled) return;
  root.classList.add('vg-motion');

  /* Sections that must never be transformed: transforms would break Horizon's
     sticky product details, the fixed sticky add-to-cart bar and the header. */
  var SKIP = '.product-information, sticky-add-to-cart, header-component, .header-section, [data-vg-no-reveal]';

  var revealIO = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        revealIO.unobserve(el);
        el.classList.add('vg-animating');
        // Next frame so the transition runs from the pending state.
        requestAnimationFrame(function () {
          el.classList.remove('vg-pending');
        });
        var done = function (e) {
          if (e && e.target !== el) return; // ignore transitions bubbling from children
          el.removeEventListener('transitionend', done);
          el.classList.remove('vg-animating');
          el.style.removeProperty('--vg-delay');
        };
        el.addEventListener('transitionend', done);
        setTimeout(done, 2400);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.01 }
  );

  function belowFold(el) {
    var r = el.getBoundingClientRect();
    return r.top > window.innerHeight * 0.92 && r.height > 0;
  }

  function prepare(el, delay) {
    if (el.dataset.vgSeen) return;
    el.dataset.vgSeen = '1';
    if (!belowFold(el)) return; // already on screen: leave it alone
    if (delay) el.style.setProperty('--vg-delay', delay + 'ms');
    el.classList.add('vg-pending');
    revealIO.observe(el);
  }

  /* 1. Elements opted in by premium sections */
  function initMarked(scope) {
    scope.querySelectorAll('[data-vg-reveal]').forEach(function (el) {
      prepare(el);
    });
  }

  /* 2. Whole sections in <main> (Theme setting) */
  function initSections() {
    if (!cfg.sections) return;
    var sections = document.querySelectorAll('#MainContent > .shopify-section');
    sections.forEach(function (section, i) {
      if (i === 0) return;
      if (section.matches(SKIP) || section.querySelector(SKIP)) return;
      if (section.querySelector('[data-vg-reveal]')) return; // has its own choreography
      prepare(section);
    });
  }

  /* 3. Product grids: stagger each batch of cards (Theme setting) */
  var GRID_ITEMS = '.product-grid__item, .resource-list__item, product-recommendations .product-card';
  function initGrids(scope) {
    if (!cfg.grids) return;
    var batch = 0;
    scope.querySelectorAll(GRID_ITEMS).forEach(function (el) {
      if (el.dataset.vgSeen) return;
      if (el.closest('dialog, .shopify-section[id*="header"]')) return;
      prepare(el, (batch++ % 4) * 80);
    });
  }

  /* 4. Parallax frames */
  var frames = [];
  var ticking = false;
  function collectFrames() {
    frames = Array.prototype.slice.call(document.querySelectorAll('[data-vg-parallax]'));
  }
  function updateParallax() {
    ticking = false;
    var vh = window.innerHeight;
    for (var i = 0; i < frames.length; i++) {
      var f = frames[i];
      var r = f.getBoundingClientRect();
      if (r.bottom < -100 || r.top > vh + 100) continue;
      var progress = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2); // -1..1
      var shift = Math.max(-1, Math.min(1, progress)) * r.height * -0.045;
      f.style.setProperty('--vg-shift', shift.toFixed(1) + 'px');
    }
  }
  function onScroll() {
    if (!ticking && frames.length) {
      ticking = true;
      requestAnimationFrame(updateParallax);
    }
  }

  function init(scope) {
    scope = scope || document;
    initMarked(scope);
    initGrids(scope);
  }

  function boot() {
    initSections();
    init(document);
    collectFrames();
    updateParallax();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });

    // Collection pagination / infinite scroll and re-rendered sections add new
    // cards; pick them up without touching Horizon's code.
    var main = document.getElementById('MainContent');
    if (main && 'MutationObserver' in window) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        requestAnimationFrame(function () {
          pending = false;
          init(main);
          collectFrames();
        });
      }).observe(main, { childList: true, subtree: true });
    }

    // If the visitor switches to reduced motion mid-visit, show everything.
    var stop = function () {
      if (!reduce.matches) return;
      root.classList.remove('vg-motion');
      document.querySelectorAll('.vg-pending').forEach(function (el) {
        el.classList.remove('vg-pending');
      });
    };
    if (reduce.addEventListener) reduce.addEventListener('change', stop);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Safety net: never leave anything hidden if the page is restored from the
  // back/forward cache or printed.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      document.querySelectorAll('.vg-pending').forEach(function (el) {
        if (!belowFold(el)) el.classList.remove('vg-pending');
      });
    }
  });
  window.addEventListener('beforeprint', function () {
    document.querySelectorAll('.vg-pending').forEach(function (el) {
      el.classList.remove('vg-pending');
    });
  });
})();
